import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ControlledCheckbox, ControlledInput, IParams, ISaveSupportType, Page, Section, Widget } from '../../../shared';
import { IncomeLimitInputs } from '../../components';
import { useSupportTypeSchema } from '../../validators';
import { useSaveSupportType, useSupportType } from '../../hooks/support-type.hook';

export function SupportTypesEditPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { id } = useParams<keyof IParams>() as IParams;
    const { data } = useSupportType(id, { messages: { onGetErrorMessage: t('getSupportTypeError') } });

    const form = useForm<ISaveSupportType>({
        resolver: yupResolver(useSupportTypeSchema()),
        mode: 'all',
    });

    useEffect(() => {
        if (data) {
            form.reset(data);
        }
    }, [data, form]);

    const { mutateAsync: saveSupportType } = useSaveSupportType();

    const onSubmit = async (data: ISaveSupportType) => {
        await saveSupportType({
            data,
            messages: {
                onUpdateSuccessMessage: t('updateSupportTypeSuccess'),
                onUpdateErrorMessage: t('updateSupportTypeError'),
                onCreateSuccessMessage: t('createSupportTypeSuccess'),
                onCreateErrorMessage: t('createSupportTypeError'),
            },
        });
        navigate('/admin/support-types');
    };

    const actions = [
        <Button onClick={() => navigate('/admin/support-types')} color="secondary">
            {t('cancel')}
        </Button>,
        <Button type="submit" variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('save')}
        </Button>,
    ];

    return (
        <Page title={data?.name || t('newSupportType')} actions={actions} onBack={() => navigate(-1)}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Stack spacing={2}>
                            <Section title={t('data')}>
                                <ControlledInput name="name" label={t('nameLabel')} required sx={{ width: '400px' }} />
                                <ControlledCheckbox name="hasCredit" label={t('hasCreditLabel')} />
                            </Section>
                            <IncomeLimitInputs />
                        </Stack>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
}
